import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';

interface UseCloseAddOnRouteChangeOptions {
	isAddOpen: boolean;
	closeAdd: () => void;
}

/**
 * Closes add mode immediately when navigation starts. Normal user closes still animate; this
 * route reset suppresses the close transition for one frame so the dock cannot paint half-open.
 */
export const useCloseAddOnRouteChange = (options: UseCloseAddOnRouteChangeOptions): boolean => {
	const { isAddOpen, closeAdd } = options;
	const pathname = useRouterState({ select: (state) => state.location.pathname });
	const isMountedRef = useRef(true);
	const prevPathnameRef = useRef(pathname);
	const [skipAddCloseTransition, setSkipAddCloseTransition] = useState(false);

	useEffect(
		() => () => {
			isMountedRef.current = false;
		},
		[],
	);

	useLayoutEffect(() => {
		if (prevPathnameRef.current === pathname) return;

		prevPathnameRef.current = pathname;
		if (!isAddOpen) return;

		setSkipAddCloseTransition(true);
		closeAdd();
		requestAnimationFrame(() => {
			if (isMountedRef.current) setSkipAddCloseTransition(false);
		});
	}, [closeAdd, isAddOpen, pathname]);

	return skipAddCloseTransition;
};
